/**
 * Native ES module produced from the historical shared-scope controller.
 * Cross-domain dependencies are explicit imports; eager DOM wiring is isolated in the init function.
 */
import { formatBytes, openNote } from '../community/events-notes-navigator.runtime.js';
import { downloadAuthorized } from '../platform/android-uikit.runtime.js';
import { $, $$, api, contextMode, copyText, escapeHTML, fmtDateTime, isoLocal, simpleMode, state, superGrokMode, toast, trackProduct } from '../platform/core.runtime.js';
import { closeDialog, openDialog, transitionDialog } from '../platform/dialog-boot.runtime.js';
import { canManageGroup, visibilityScopeFieldHTML } from '../session/session.runtime.js';
import { confirmAction, headAction, loadCatalogs, options, renderView, requireGroup, resetHeadActions, setView } from '../shell/navigation.runtime.js';
import { aiBatchTelegramMeta, aiGlobalBatchKey, aiGlobalBatchProgressHTML, aiHomeworkStageMeta, aiHomeworkStorageKey, openAIGlobalSolveAll, openAIHomeworkSolve, openAIHomeworkSolveAll, pageToolbarHTML, sgSubpageHeadHTML, wirePageToolbar, wireSgSubpageBack } from './homework-ai.runtime.js';

function aiGlobalBatchResultHTML(batch){
  const rows=(batch.items||[]).map(item=>`<button class="ai-global-result-row ${item.status==='failed'?'failed':''}" type="button" data-ai-global-job="${item.job_id}" data-subject="${item.subject_id}"><span>${item.status==='completed'?'✓':'!'}</span><div><strong>${escapeHTML(item.subject_name)}</strong><small>${item.status==='completed'?`${item.homework_count} заданий · решение готово`:escapeHTML(item.last_error||'Не удалось решить')}</small></div><b>→</b></button>`).join('');
  const pdf=batch.pdf_status==='ready'?`<div class="ai-global-pdf-card"><span>PDF</span><div><strong>Все решения одним файлом</strong><small>${escapeHTML(batch.pdf_original_name||'kUni-homework-solutions.pdf')} · ${batch.pdf_size_bytes?formatBytes(batch.pdf_size_bytes):'готово'}</small></div><button class="button small primary" type="button" data-ai-batch-pdf>Скачать PDF</button></div>`:`<div class="ai-global-pdf-card ${batch.pdf_status==='failed'?'failed':''}"><span>PDF</span><div><strong>${batch.pdf_status==='failed'?'PDF не собрался':'Собираю общий PDF'}</strong><small>${escapeHTML(batch.pdf_error||'Файл появится автоматически после финализации.')}</small></div></div>`;
  return `<div class="ai-global-result" data-ai-batch="${batch.id}"><div class="ai-result-head"><span>✦</span><div><strong>Домашка по всем предметам</strong><small>${batch.completed_jobs}/${batch.total_jobs} предметов решено${batch.failed_jobs?` · ${batch.failed_jobs} с ошибкой`:''}</small></div></div>${pdf}<div class="ai-global-results-list">${rows}</div><div class="ai-result-meta">${aiBatchTelegramMeta(batch)}<span class="ai-answer-warning">ИИ может ошибаться — перед сдачей проверь вычисления.</span></div><div class="ai-result-actions"><button class="button small" type="button" data-ai-global-new>Решить всё заново</button></div></div>`;
}

function bindAIGlobalBatchActions(batch){
  $('[data-ai-batch-pdf]')?.addEventListener('click',()=>downloadAuthorized(`/ai/homework/batches/${encodeURIComponent(batch.id)}/pdf`,batch.pdf_original_name||'kUni-homework-solutions.pdf').catch(error=>toast(error.message)));
  $$('[data-ai-global-job]').forEach(btn=>btn.addEventListener('click',()=>{const item=(batch.items||[]).find(v=>v.job_id===btn.dataset.aiGlobalJob);if(!item)return;transitionDialog(()=>openAIHomeworkJob(item.job_id,{id:item.job_id,title:`Все задания · ${item.subject_name}`,subject_id:item.subject_id,subject_name:item.subject_name,schedule_entry_id:null,ai_batch:true,ai_global_child:true,ai_global_batch_id:batch.id,ai_items:[]}));}));
  $('[data-ai-global-new]')?.addEventListener('click',async()=>{localStorage.removeItem(aiGlobalBatchKey());const items=await api(`/groups/${state.groupID}/homework?include_archived=false`);transitionDialog(()=>openAIGlobalSolveAll(items,{forceNew:true}).catch(error=>toast(error.message)));});
}

export async function openAIGlobalBatch(batchOrID){
  let batch=typeof batchOrID==='object'?batchOrID:await api(`/ai/homework/batches/${encodeURIComponent(batchOrID)}`);
  const terminal=['completed','partial','failed'].includes(batch.status);
  if(terminal&&['ready','failed'].includes(batch.pdf_status)){
    openDialog('Решить всё',aiGlobalBatchResultHTML(batch),[{label:'Готово',primary:true,action:closeDialog}]);bindAIGlobalBatchActions(batch);return;
  }
  openDialog('Решаю всё',aiGlobalBatchProgressHTML(batch),[{label:'Скрыть',action:closeDialog}]);
  pollAIGlobalBatch(batch.id);
}

async function pollAIGlobalBatch(batchID){
  const dialog=$('#dialog');
  const tick=async()=>{if(!dialog?.open||!$(`[data-ai-batch="${batchID}"]`))return;try{const batch=await api(`/ai/homework/batches/${encodeURIComponent(batchID)}`);const terminal=['completed','partial','failed'].includes(batch.status)&&['ready','failed'].includes(batch.pdf_status);if(terminal){openAIGlobalBatch(batch);return;}const root=$(`[data-ai-batch="${batchID}"]`);if(root)root.outerHTML=aiGlobalBatchProgressHTML(batch);setTimeout(tick,1700);}catch(error){console.warn('AI global batch poll failed',error);setTimeout(tick,3200);}};
  setTimeout(tick,1100);
}

async function openAISolveAllChooser(items){return openAIGlobalSolveAll(items);}

function aiHomeworkProgressHTML(job){
  const [label,percent]=aiHomeworkStageMeta(job.stage);
  const type=({direct:'Прямой ответ',math:'Вычисления',document_rag:'Локальные источники / RAG',web_research:'Web research',document_and_web:'Источники + web'})[job.task_type]||'Определяется';
  const attempt=Number(job.attempts||0),maxAttempts=Number(job.max_attempts||3);const retryAt=job.status==='queued'&&attempt>0&&job.available_at&&new Date(job.available_at)>new Date()?` · повтор ${fmtDateTime(job.available_at)}`:'';
  return `<div class="ai-homework-progress" data-ai-job="${job.id}"><div class="ai-homework-brand"><span class="ai-spark">✦</span><div><strong>${escapeHTML(label)}</strong><small>${escapeHTML(job.current_detail||job.homework_text||'')}</small></div></div><div class="ai-progress-track"><i style="width:${percent}%"></i></div><div class="ai-stage-row"><span class="${percent>=16?'done':''}">Router</span><span class="${percent>=48?'done':''}">Контекст</span><span class="${percent>=68?'done':''}">Источники</span><span class="${percent>=92?'done':''}">Проверка</span></div><div class="ai-live-meta"><span>${escapeHTML(type)}</span><span>${job.key_source==='byok'?'BYOK':job.key_source&&job.key_source!=='unresolved'?'server key':'ключ определяется'}</span><span>попытка ${Math.max(1,attempt)}/${maxAttempts}</span>${job.search_count?`<span>${job.search_count} web-поисков</span>`:''}</div>${job.last_error&&job.status==='queued'?`<p class="ai-retry-note">Временная ошибка: ${escapeHTML(job.last_error)}${escapeHTML(retryAt)}</p>`:''}<p class="ai-homework-wait">Можно закрыть окно — очередь и lease переживут рестарт worker. При временной ошибке kUni попробует снова автоматически.</p></div>`;
}

function renderAIMathText(value){
  return escapeHTML(String(value||'').replace(/\\cdot/g,'·').replace(/\\times/g,'×').replace(/\\pm/g,'±').replace(/\\leq?/g,'≤').replace(/\\geq?/g,'≥').replace(/\\neq/g,'≠').replace(/\\approx/g,'≈').replace(/\\infty/g,'∞').replace(/\\Delta/g,'Δ').replace(/\\theta/g,'θ').replace(/\\lambda/g,'λ').replace(/\\mu/g,'μ').replace(/\\pi/g,'π'));
}

function renderAIAnswerMarkdown(source=''){
  const inline=(value)=>{
    let out=escapeHTML(value);
    out=out.replace(/`([^`]+)`/g,'<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g,'<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g,'<em>$1</em>')
      .replace(/\[([^\]\n]{1,120}),\s*стр\.\s*(\d+)\]/gi,'<span class="ai-page-ref">$1 · стр. $2</span>')
      .replace(/\[стр\.\s*(\d+)\]/gi,'<span class="ai-page-ref">стр. $1</span>')
      .replace(/\$([^$\n]+)\$/g,(_,m)=>`<span class="ai-inline-math">${renderAIMathText(m)}</span>`);
    return out;
  };
  const lines=String(source).replace(/\r/g,'').split('\n');let html='';let list='';let inCode=false,code=[];
  const closeList=()=>{if(list){html+=`</${list}>`;list='';}};
  for(let i=0;i<lines.length;i++){
    const raw=lines[i],line=raw.trimEnd(),trim=line.trim();
    if(trim.startsWith('```')){closeList();if(inCode){html+=`<pre class="ai-code"><code>${escapeHTML(code.join('\n'))}</code></pre>`;code=[];inCode=false;}else inCode=true;continue;}
    if(inCode){code.push(raw);continue;}
    if(trim==='$$'){closeList();const math=[];for(i++;i<lines.length&&lines[i].trim()!=='$$';i++)math.push(lines[i]);html+=`<div class="ai-math-block">${renderAIMathText(math.join(' '))}</div>`;continue;}
    if(!trim){closeList();continue;}
    if(/^\|.+\|$/.test(trim)&&i+1<lines.length&&/^\|?[\s:|-]+\|?$/.test(lines[i+1].trim())){closeList();const rows=[];rows.push(trim);i+=2;while(i<lines.length&&/^\|.+\|$/.test(lines[i].trim())){rows.push(lines[i].trim());i++;}i--;const cells=rows.map(r=>r.replace(/^\||\|$/g,'').split('|').map(c=>c.trim()));html+='<div class="ai-table-wrap"><table><thead><tr>'+cells[0].map(c=>`<th>${inline(c)}</th>`).join('')+'</tr></thead><tbody>'+cells.slice(1).map(row=>'<tr>'+row.map(c=>`<td>${inline(c)}</td>`).join('')+'</tr>').join('')+'</tbody></table></div>';continue;}
    const heading=trim.match(/^(#{1,4})\s+(.+)$/);if(heading){closeList();const text=heading[2].trim(),kind=/^дано[:.]?$/i.test(text)?'given':/^решение[:.]?$/i.test(text)?'solution':/^ответ[:.]?$/i.test(text)?'answer':'';html+=`<h3 class="ai-section-title ${kind?`is-${kind}`:''}">${kind?`<span>${kind==='given'?'Д':kind==='solution'?'→':'✓'}</span>`:''}${inline(text)}</h3>`;continue;}
    const bullet=trim.match(/^[-*]\s+(.+)$/),ordered=trim.match(/^\d+[.)]\s+(.+)$/);if(bullet||ordered){const tag=ordered?'ol':'ul';if(list!==tag){closeList();list=tag;html+=`<${tag}>`;}html+=`<li>${inline((ordered||bullet)[1])}</li>`;continue;}
    if(trim.startsWith('>')){closeList();html+=`<blockquote>${inline(trim.replace(/^>\s?/,''))}</blockquote>`;continue;}
    if(/^---+$/.test(trim)){closeList();html+='<hr>';continue;}
    html+=`<p>${inline(trim)}</p>`;
  }
  closeList();if(inCode&&code.length)html+=`<pre class="ai-code"><code>${escapeHTML(code.join('\n'))}</code></pre>`;
  return html||'<p>Пустой ответ.</p>';
}

function aiResultTelegramMeta(job){
  if(!job.send_telegram)return '';
  if(job.telegram_sent_at)return '<span class="ai-delivery-status good">✓ PDF отправлен в Telegram</span>';
  if(job.telegram_last_error)return `<span class="ai-delivery-status warn">Telegram: ${escapeHTML(job.telegram_last_error)}</span>`;
  return '<span class="ai-delivery-status">PDF ожидает отправки в Telegram</span>';
}

function bindAIHomeworkResultActions(job,item){
  $('[data-ai-copy]')?.addEventListener('click',()=>copyText(job.answer_md,'Решение скопировано').catch(error=>toast(error.message)));
  $('[data-ai-new]')?.addEventListener('click',()=>{if(item.ai_global_child&&item.ai_global_batch_id){transitionDialog(()=>openAIGlobalBatch(item.ai_global_batch_id).catch(error=>toast(error.message)));return;}localStorage.removeItem(aiHomeworkStorageKey(item));transitionDialog(()=>item.ai_batch?openAIHomeworkSolveAll({id:item.subject_id,name:item.subject_name,openHomework:item.ai_items||[]}).catch(error=>toast(error.message)):openAIHomeworkSolve(item,{forceNew:true}));});
  $('[data-ai-save]')?.addEventListener('click',()=>openAISaveSolutionDialog(job,item));
  $('[data-ai-open-note]')?.addEventListener('click',()=>{const id=$('[data-ai-open-note]')?.dataset.aiOpenNote||job.saved_note_id;if(id){closeDialog();openNote(id).catch(e=>toast(e.message));}});
  $('[data-ai-open-material]')?.addEventListener('click',async()=>{
    const id=$('[data-ai-open-material]')?.dataset.aiOpenMaterial||job.saved_material_id;
    if(!id)return;
    try{
      const materials=await api(`/groups/${state.groupID}/materials?include_archived=false`);
      const material=(materials||[]).find(v=>v.id===id);
      closeDialog();
      if(material?.original_name){await downloadAuthorized(`/materials/${encodeURIComponent(id)}/download?viewer_group_id=${encodeURIComponent(state.groupID)}`,material.original_name);return;}
      if(material?.url){window.open(material.url,'_blank','noopener,noreferrer');return;}
      await setView('materials');
    }catch(e){toast(e.message);}
  });
}

async function retryAIHomeworkJob(job,item){
  const retried=await api(`/ai/homework/jobs/${encodeURIComponent(job.id)}/retry`,{method:'POST'});toast('Job снова поставлена в очередь');return openAIHomeworkJob(retried,item);
}

async function cancelAIHomeworkJob(job,item){
  if(!confirmAction('Отменить эту AI job? Уже выполненный вызов модели может закончиться, но результат больше не будет принят.'))return;
  const cancelled=await api(`/ai/homework/jobs/${encodeURIComponent(job.id)}/cancel`,{method:'POST'});toast(cancelled.status==='cancelled'?'Job отменена':'Отмена запрошена');return openAIHomeworkJob(cancelled,item);
}

function openAISaveSolutionDialog(job,item){
  const defaultTitle=item.ai_batch?`Решения — ${item.subject_name}`:`Решение — ${item.title}`;
  const canAttach=Boolean(!item.ai_batch&&item.schedule_entry_id);
  openDialog('Сохранить решение',`<form class="form" id="ai-save-note-form"><label>Название<input name="title" maxlength="180" required value="${escapeHTML(defaultTitle)}"></label><label>Куда сохранить<select name="destination"><option value="solution" selected>Решения</option><option value="note">Конспекты</option><option value="material">Материалы</option></select></label><p class="muted" data-ai-save-destination-hint>Отдельное готовое решение. Источник AI и исходная задача сохранятся в метаданных.</p>${visibilityScopeFieldHTML('group')}${canAttach?'<label class="row clickable" data-ai-save-lesson-row><input class="check" type="checkbox" name="attach_to_lesson" checked><div class="row-main"><strong>Привязать к этой паре</strong><p>Для друзей kUni всё равно найдёт ближайшую пару по предмету в их расписании.</p></div></label>':''}</form>`,[
    {label:'Отмена',action:closeDialog},
    {label:'Сохранить',primary:true,action:async()=>{
      const form=$('#ai-save-note-form');if(!form?.reportValidity())return;
      const data=Object.fromEntries(new FormData(form));
      const destination=data.destination||'solution';
      const saved=await api(`/ai/homework/jobs/${encodeURIComponent(job.id)}/save`,{method:'POST',body:JSON.stringify({title:data.title,destination,attach_to_lesson:destination!=='material'&&canAttach&&form.attach_to_lesson?.checked===true,visibility_scope:data.visibility_scope||'group'})});
      if(saved.destination==='material'){
        job.saved_material_id=saved.id;job.saved_note_id=null;job.saved_destination='material';
      }else{
        job.saved_note_id=saved.id;job.saved_material_id=null;job.saved_destination=saved.destination||destination;
      }
      trackProduct('ai.homework_solution_saved',{subject_id:item.subject_id,destination:saved.destination||destination});
      const targetLabel=({solution:'Решение сохранено в «Решения»',note:'Решение сохранено в конспекты',material:'Решение сохранено в материалы'})[saved.destination||destination]||'Решение сохранено';
      toast(targetLabel);closeDialog();openAIHomeworkJob(job,item).catch(error=>toast(error.message));
    }}
  ]);
  const form=$('#ai-save-note-form');
  const destination=form?.elements?.destination;
  const hint=$('[data-ai-save-destination-hint]',form);
  const lessonRow=$('[data-ai-save-lesson-row]',form);
  const sync=()=>{
    const value=destination?.value||'solution';
    if(hint)hint.textContent=({solution:'Отдельное готовое решение. Источник AI и исходная задача сохранятся в метаданных.',note:'Сохранится как конспект, но останется помечено как созданное из AI-решения.',material:'Сохранится как файловый материал (.md) с происхождением из AI-решения.'})[value];
    if(lessonRow)lessonRow.hidden=value==='material';
  };
  destination?.addEventListener('change',sync);sync();
}

function aiResultContextChips(job,item){
  const chips=[];
  if(item.subject_name) chips.push(item.subject_name);
  chips.push(job.trigger_source==='autopilot'?'Автопилот':item.ai_batch?'Пакет заданий':'Один проход');
  chips.push(`${Number(job.attempts||1)} попытка`);
  if((job.web_sources||[]).length) chips.push(`${job.web_sources.length} web`);
  return chips.map((c)=>`<span class="ai-chip">${escapeHTML(c)}</span>`).join('');
}

function aiResultNoticesHTML(job){
  const notices=[];
  const answer=String(job.answer_md||'');
  // Surface known system warnings as structured banners instead of burying them in markdown.
  const webLimit=/веб-проверк|web[_ ]?search|лимит web/i.test(answer)||job.web_search_limited||job.web_degraded;
  if(webLimit && !(job.web_sources||[]).length){
    notices.push(`<div class="ai-notice warn"><span>!</span><div><strong>Веб-проверка недоступна</strong><p>Ответ без свежего интернет-поиска. Актуальные факты стоит перепроверить вручную.</p></div></div>`);
  }
  const guideSources=(job.sources||[]).filter((s)=>s.type==='teacher_guide');
  if(guideSources.length){
    notices.push(`<div class="ai-notice guide"><span>◎</span><div><strong>Учтён гайд преподавателя</strong><p>${escapeHTML(guideSources[0].title||'Wiki-гайд')}</p></div></div>`);
  }
  const localSources=(job.sources||[]).filter((s)=>s.type==='note');
  if(localSources.length){
    notices.push(`<div class="ai-notice sources"><span>📄</span><div><strong>Локальные источники · ${localSources.length}</strong><p>${localSources.map((s)=>escapeHTML(s.title||s.original_name||'файл')).join(' · ')}${(job.source_pages||[]).length?` · стр. ${(job.source_pages||[]).join(', ')}`:''}</p></div></div>`);
  } else if((job.source_pages||[]).length){
    notices.push(`<div class="ai-notice sources"><span>📄</span><div><strong>Страницы</strong><p>${(job.source_pages||[]).map((p)=>`стр. ${p}`).join(', ')}</p></div></div>`);
  }
  return notices.join('');
}

function stripAISystemBannersFromMarkdown(source=''){
  // Remove leading system-warning paragraphs that we already show as structured notices.
  return String(source||'')
    .replace(/^(?:\s*>?\s*)?(?:⚠|⚠️)?\s*Веб-проверк[^\n]*(?:\n(?!\n|#)[^\n]*)*\n+/i,'')
    .replace(/^(?:\s*>?\s*)?Web[- ]?search[^\n]*(?:\n(?!\n|#)[^\n]*)*\n+/i,'');
}

export async function openAIHomeworkJob(jobID,item){
  let job=typeof jobID==='object'?jobID:await api(`/ai/homework/jobs/${encodeURIComponent(jobID)}`);
  item=item||{id:job.homework_id,title:'kUni AI',subject_id:'',subject_name:'',schedule_entry_id:''};
  const renderJob=()=>{
    if(job.status==='completed'){
      const noteSavedLabel=job.saved_destination==='solution'?'✓ В решениях':job.saved_destination==='note'?'✓ В конспектах':'✓ Сохранено';
      const saved=job.saved_material_id
        ?`<button class="button small" type="button" data-ai-open-material="${job.saved_material_id}">✓ В материалах</button>`
        :job.saved_note_id
          ?`<button class="button small" type="button" data-ai-open-note="${job.saved_note_id}">${noteSavedLabel}</button>`
          :`<button class="button small" type="button" data-ai-save>Сохранить</button>`;
      const webHTML=(job.web_sources||[]).length
        ?`<details class="ai-web-sources"><summary>Интернет-источники · ${job.web_sources.length}</summary><div>${job.web_sources.map((source,i)=>{
          const url=String(source.url||'');
          const safe=/^https:\/\//i.test(url)?escapeHTML(url):'';
          const quality=escapeHTML(source.quality||'C');
          const title=escapeHTML(source.title||source.source||'Источник');
          return safe
            ?`<a href="${safe}" target="_blank" rel="noopener noreferrer"><span>${i+1}</span><strong>${title}</strong><small class="ai-source-quality">${quality}</small></a>`
            :`<div><span>${i+1}</span><strong>${title}</strong><small class="ai-source-quality">${quality}</small></div>`;
        }).join('')}</div></details>`
        :'';
      const answerBody=renderAIAnswerMarkdown(stripAISystemBannersFromMarkdown(job.answer_md));
      openDialog('Решение',`<div class="ai-homework-result ai-result-v2" data-ai-job="${job.id}">
        <header class="ai-result-hero">
          <div class="ai-result-hero-mark" aria-hidden="true">✦</div>
          <div class="ai-result-hero-copy">
            <strong>${escapeHTML(item.title||'Решение')}</strong>
            <div class="ai-chip-row">${aiResultContextChips(job,item)}</div>
          </div>
        </header>
        ${aiResultNoticesHTML(job)}
        ${webHTML}
        <article class="ai-answer-markdown">${answerBody}</article>
        <footer class="ai-result-footer">
          <div class="ai-result-status">${aiResultTelegramMeta(job)}<span class="ai-answer-warning">ИИ может ошибаться — перед сдачей проверь числа и требования.</span></div>
          <div class="ai-result-actions">
            <button class="button small" type="button" data-ai-copy>Скопировать</button>
            ${saved}
            <button class="button small" type="button" data-ai-new>Заново</button>
            <button class="button primary small" type="button" data-ai-done>Готово</button>
          </div>
        </footer>
      </div>`,[]);
      const footer=$('#dialog-footer'); if(footer) footer.hidden=true;
      $('[data-ai-done]')?.addEventListener('click',()=>closeDialog());
      bindAIHomeworkResultActions(job,item);
      const trackedKey=`kuni.ai-homework.completed.${job.id}`;
      if(!localStorage.getItem(trackedKey)){trackProduct('ai.homework_solve_completed',{subject_id:item.subject_id||''});localStorage.setItem(trackedKey,'1');}
      return;
    }
    if(job.status==='failed'||job.status==='cancelled'){
      const cancelled=job.status==='cancelled';const reason=cancelled?'Эта генерация была отменена.':(job.failure_reason||job.last_error||'Mistral не вернул результат.');
      openDialog(cancelled?'Генерация отменена':'Не получилось',`<div class="ai-homework-empty ${cancelled?'cancelled':''}" data-ai-job="${job.id}"><span>${cancelled?'×':'!'}</span><strong>${cancelled?'Job остановлена':'Не удалось решить'}</strong><p>${escapeHTML(reason)}</p><small>Попыток: ${Number(job.attempts||0)} / ${Number(job.max_attempts||3)}. Повтор использует ту же задачу, источники и настройки Web Search.</small><div class="ai-terminal-actions"><button class="button primary small" type="button" data-ai-retry>Повторить эту job</button><button class="button small" type="button" data-ai-new>Создать новую</button></div></div>`,[{label:'Закрыть',action:closeDialog}]);
      $('[data-ai-retry]')?.addEventListener('click',()=>retryAIHomeworkJob(job,item).catch(e=>toast(e.message)));
      $('[data-ai-new]')?.addEventListener('click',()=>{if(item.ai_global_child&&item.ai_global_batch_id){transitionDialog(()=>openAIGlobalBatch(item.ai_global_batch_id).catch(error=>toast(error.message)));return;}localStorage.removeItem(aiHomeworkStorageKey(item));transitionDialog(()=>item.ai_batch?openAIHomeworkSolveAll({id:item.subject_id,name:item.subject_name,openHomework:item.ai_items||[]}).catch(error=>toast(error.message)):openAIHomeworkSolve(item,{forceNew:true}));});return;
    }
    openDialog('Решаю домашку',aiHomeworkProgressHTML(job),[{label:'Скрыть',action:closeDialog},{label:job.cancel_requested_at?'Отмена запрошена':'Отменить',danger:true,action:()=>cancelAIHomeworkJob(job,item).catch(e=>toast(e.message))}]);
    pollAIHomeworkJob(job.id,item);
  };
  renderJob();
}

async function pollAIHomeworkJob(jobID,item){
  const dialog=$('#dialog');
  const tick=async()=>{
    if(!dialog?.open || !$(`[data-ai-job="${jobID}"]`))return;
    try{
      const job=await api(`/ai/homework/jobs/${encodeURIComponent(jobID)}`);
      if(['completed','failed','cancelled'].includes(job.status)){openAIHomeworkJob(job,item);return;}
      const root=$(`[data-ai-job="${jobID}"]`);if(root)root.outerHTML=aiHomeworkProgressHTML(job);
      const fastStage=['queued','loading','routing'].includes(job.stage);
      setTimeout(tick,fastStage?650:1200);
    }catch(error){console.warn('AI homework poll failed',error);setTimeout(tick,2500);}
  };
  // The worker now picks interactive jobs up sub-second. Poll early so the UI
  // does not keep showing the stale loading label while Router/Solver is active.
  setTimeout(tick,180);
}

function requirementItemTypeLabel(type) {
  return ({checkbox:'Готово / не готово',counter:'Счётчик',percent:'Процент',score:'Оценка / балл'})[type] || 'Условие';
}

function requirementUnit(item) {
  if (item.unit) return item.unit;
  if (item.item_type === 'percent') return '%';
  if (item.item_type === 'score') return 'балла';
  return '';
}

export function requirementValueText(item) {
  if (item.item_type === 'checkbox') return item.completed ? 'Выполнено' : 'Не выполнено';
  const unit=requirementUnit(item); const current=Number(item.current_value||0); const target=Number(item.target_value||0); const max=item.max_value==null?null:Number(item.max_value);
  return `${current}${unit?` ${unit}`:''} / ${target}${unit?` ${unit}`:''}${max&&max!==target?` · максимум ${max}`:''}`;
}

function requirementItemPercent(item) {
  const target=Math.max(.0001,Number(item.target_value||1));
  return Math.max(0,Math.min(100,Math.round(Number(item.current_value||0)/target*100)));
}

function requirementTargetGrade(item) {
  const grade=Number(item?.target_grade);
  return grade===3||grade===4||grade===5?grade:5;
}

function requirementGradeWord(grade) {
  return ({2:'неуд.',3:'удовлетворительно',4:'хорошо',5:'отлично'})[Number(grade)] || 'оценка';
}

function requirementItemsByGrade(items) {
  const by={3:[],4:[],5:[]};
  (items||[]).forEach((item)=>by[requirementTargetGrade(item)].push(item));
  return by;
}

export function requirementGradeState(reqOrItems, liveRows) {
  const by={3:[],4:[],5:[]};
  if (Array.isArray(liveRows)) {
    liveRows.forEach((row)=>{
      const raw=Number(row.dataset.targetGrade);
      const grade=raw===3||raw===4||raw===5?raw:5;
      by[grade].push({completed:row.classList.contains('complete')});
    });
  } else {
    const items=Array.isArray(reqOrItems)?reqOrItems:(reqOrItems?.items||[]);
    items.forEach((item)=>by[requirementTargetGrade(item)].push(item));
  }
  let grade=2;
  for (const g of [3,4,5]) {
    if (by[g].length && by[g].every((item)=>item.completed)) grade=g;
  }
  const total=[3,4,5].reduce((sum,g)=>sum+by[g].length,0);
  const done=[3,4,5].reduce((sum,g)=>sum+by[g].filter((item)=>item.completed).length,0);
  const top=[5,4,3].find((g)=>by[g].length)||5;
  let next=null;
  for (const g of [3,4,5]) {
    if (g>grade && by[g].length) { next=g; break; }
  }
  let percent=((grade-2)/3)*100;
  if (next) {
    const list=by[next];
    percent += (list.filter((item)=>item.completed).length/list.length)*(100/3);
  } else if (grade===2) {
    const lowest=[3,4,5].find((g)=>by[g].length);
    if (lowest) {
      const list=by[lowest];
      percent = (list.filter((item)=>item.completed).length/list.length)*(((lowest-2)/3)*100);
    }
  } else if (grade>=top) {
    percent=100;
  }
  percent=Math.max(0,Math.min(100,Math.round(percent)));
  return {grade,percent,by,next,top,done,total,complete:total>0 && grade>=top};
}

function requirementGradeScaleHTML(state) {
  const grade=state.grade;
  const ready=state.complete && grade>=5;
  const hint=state.next?`${state.by[state.next].filter((item)=>item.completed).length}/${state.by[state.next].length} на ${state.next}`:`${state.percent}%`;
  return `<div class="requirement-grade">
    <div class="requirement-grade-head"><strong>${ready?`Автомат ${grade}`:`Оценка ${grade}`}</strong><small>${escapeHTML(hint)}</small></div>
    <div class="requirement-grade-scale" role="meter" aria-valuemin="2" aria-valuemax="5" aria-valuenow="${grade}">${[2,3,4,5].map((g)=>`<span class="${g<=grade?'on':''}${g===grade?' current':''}">${g}</span>`).join('')}</div>
    <div class="progress-line requirement-grade-bar"><span style="width:${state.percent}%"></span></div>
  </div>`;
}

function requirementPayloadItem(item) {
  const payload={title:item.title,required:item.required!==false,item_type:item.item_type||'checkbox',target_grade:requirementTargetGrade(item),target_value:Number(item.target_value||1),max_value:item.max_value==null?null:Number(item.max_value),unit:item.unit||''};
  if (item.id) payload.id=item.id;
  return payload;
}

function requirementConditionCardHTML(item) {
  const grade=requirementTargetGrade(item);
  return `<div class="requirement-condition ${item.completed?'complete':''}" data-condition-id="${item.id}" data-target-grade="${grade}" data-target-value="${Number(item.target_value||1)}">
    <div class="requirement-condition-main"><span class="requirement-condition-icon">${item.completed?'✓':item.item_type==='checkbox'?'○':'#'}</span><div><strong>${escapeHTML(item.title)}</strong><small>${escapeHTML(requirementItemTypeLabel(item.item_type))} · на ${grade}</small></div><b>${escapeHTML(requirementValueText(item))}</b></div>
    <div class="condition-progress"><span style="width:${requirementItemPercent(item)}%"></span></div>
    ${item.item_type==='checkbox'
      ? `<label class="requirement-toggle"><input class="requirement-check" data-id="${item.id}" type="checkbox" ${item.completed?'checked':''}><span>${item.completed?'Выполнено':'Отметить выполненным'}</span></label>`
      : `<div class="requirement-value-editor"><label>Текущее значение<input class="requirement-value" data-id="${item.id}" type="number" min="0" ${item.max_value!=null?`max="${Number(item.max_value)}"`:''} step="${item.item_type==='score'?'0.1':'1'}" value="${Number(item.current_value||0)}"></label><button class="button small requirement-value-save" type="button" data-id="${item.id}">Сохранить</button></div>`}
  </div>`;
}

function requirementGroupedListHTML(req) {
  const by=requirementItemsByGrade(req.items);
  return `<div class="requirement-condition-list">${[5,4,3].map((g)=>{
    if (!by[g].length) return '';
    const done=by[g].filter((item)=>item.completed).length;
    return `<section class="requirement-grade-group" data-grade="${g}"><header class="requirement-grade-group-head"><span class="requirement-grade-badge">${g}</span><div><strong>На ${g}</strong><small>${done}/${by[g].length} · ${requirementGradeWord(g)}</small></div></header>${by[g].map(requirementConditionCardHTML).join('')}</section>`;
  }).join('')}</div>`;
}

function paintRequirementCard(card) {
  const state=requirementGradeState(null, $$('.requirement-condition', card));
  const score=card.querySelector('.requirement-v2-score b');
  const hint=card.querySelector('.requirement-v2-score small');
  const gradeStrong=card.querySelector('.requirement-grade-head strong');
  const gradeSmall=card.querySelector('.requirement-grade-head small');
  const gradeBar=card.querySelector('.requirement-grade-bar span');
  if (score) score.textContent=String(state.grade);
  if (hint) hint.textContent=state.complete?'Автомат готов':(state.next?`ещё на ${state.next}`:'набор на оценку');
  if (gradeStrong) gradeStrong.textContent=state.complete&&state.grade>=5?`Автомат ${state.grade}`:`Оценка ${state.grade}`;
  if (gradeSmall) gradeSmall.textContent=state.next?`${state.by[state.next].filter((item)=>item.completed).length}/${state.by[state.next].length} на ${state.next}`:`${state.percent}%`;
  if (gradeBar) gradeBar.style.width=`${state.percent}%`;
  $$('.requirement-grade-scale span', card).forEach((el)=>{
    const g=Number(el.textContent);
    el.classList.toggle('on', g<=state.grade);
    el.classList.toggle('current', g===state.grade);
  });
}

export async function renderRequirements() {
  resetHeadActions();
  if(!requireGroup())return;
  const usePageToolbar = superGrokMode() || contextMode() || simpleMode() || window.matchMedia('(max-width:760px)').matches;
  if(canManageGroup() && !usePageToolbar) headAction('Добавить автомат',()=>openRequirementDialog());
  const items=await api(`/groups/${state.groupID}/requirements?include_archived=${canManageGroup()}`); const active=items.filter(v=>v.status!=='archived');
  const head = superGrokMode()
    ? `${sgSubpageHeadHTML('Автомат', 'Условия')}${canManageGroup()&&usePageToolbar?pageToolbarHTML([{id:'add',label:'Добавить автомат',primary:true}]):''}`
    : (canManageGroup()&&usePageToolbar?pageToolbarHTML([{id:'add',label:'Добавить автомат',primary:true}]):'');
  const body = !active.length
    ? '<div class="empty"><strong>Требования не заданы</strong>Добавьте условия на 5, 4 и 3: лабораторные, посещаемость или баллы.</div>'
    : active.map((req)=>{
      const state=requirementGradeState(req);
      return `<section class="requirement-v2"><header class="requirement-v2-head ${canManageGroup()?'clickable requirement-manage':''}" data-id="${req.id}"><div><span>${escapeHTML(req.subject_name)}</span><strong>${escapeHTML(req.title)}</strong><p>${escapeHTML(req.description||'')}${req.valid_until?` · до ${fmtDateTime(req.valid_until)}`:''}</p></div><div class="requirement-v2-score"><b>${state.grade}</b><small>${state.complete?'Автомат готов':(state.next?`ещё на ${state.next}`:'набор на оценку')}</small></div>${requirementGradeScaleHTML(state)}</header>${requirementGroupedListHTML(req)}</section>`;
    }).join('');
  $('#content').innerHTML=`<div class="${superGrokMode()?'sg-page':''}">${head}${body}</div>`;
  wireSgSubpageBack();
  wirePageToolbar({ add: () => openRequirementDialog() });
  const paintRequirementProgress = () => $$('.requirement-v2').forEach(paintRequirementCard);
  $$('.requirement-check').forEach(box=>box.addEventListener('change',async()=>{
    const row = box.closest('.requirement-condition');
    try{
      await api(`/requirement-items/${box.dataset.id}/progress`,{method:'PUT',body:JSON.stringify({value:box.checked?1:0})});
      row?.classList.toggle('complete', box.checked);
      const icon = row?.querySelector('.requirement-condition-icon');
      if (icon) icon.textContent = box.checked ? '✓' : '○';
      const label = box.closest('label')?.querySelector('span:last-child');
      if (label) label.textContent = box.checked ? 'Выполнено' : 'Отметить выполненным';
      paintRequirementProgress();
    }catch(error){box.checked=!box.checked;toast(error.message);}
  }));
  $$('.requirement-value-save').forEach(button=>button.addEventListener('click',async()=>{
    const row=button.closest('.requirement-condition');
    const input=row?.querySelector('.requirement-value') || $(`.requirement-value[data-id="${button.dataset.id}"]`);
    const value=Number(input?.value);
    if(!Number.isFinite(value)||value<0){toast('Введите корректное значение');return;}
    try{
      await api(`/requirement-items/${button.dataset.id}/progress`,{method:'PUT',body:JSON.stringify({value})});
      const target=Number(row?.dataset.targetValue||1);
      if(row){
        row.classList.toggle('complete', value>=target);
        const bar=row.querySelector('.condition-progress span');
        if(bar) bar.style.width=`${Math.max(0,Math.min(100,Math.round(value/Math.max(.0001,target)*100)))}%`;
      }
      paintRequirementProgress();
      toast('Сохранено');
    }catch(error){toast(error.message);}
  }));
  if(canManageGroup())$$('.requirement-manage').forEach(row=>row.addEventListener('click',()=>openRequirementManager(active.find(v=>v.id===row.dataset.id))));
}

async function openRequirementManager(req){
  const state=requirementGradeState(req);
  openDialog('Автомат',`<div class="requirement-manager-summary"><span>${escapeHTML(req.subject_name)}</span><strong>${escapeHTML(req.title)}</strong><p>${escapeHTML(req.description||'Без описания')}</p>${requirementGradeScaleHTML(state)}<small>Сейчас ${state.grade} · ${state.done}/${state.total} условий выполнено</small></div>`,[
    {label:'Прогресс группы',action:async()=>{const rows=await api(`/requirements/${req.id}/progress`);openDialog('Прогресс группы',rows.length?`<div class="list">${rows.map(v=>`<div class="row"><div class="row-main"><strong>${escapeHTML(v.display_name)}</strong><p>Обязательные: ${v.required_done}/${v.required_total} · всего: ${v.done}/${v.total}</p></div></div>`).join('')}</div>`:'<div class="empty">Нет участников</div>',[{label:'Закрыть',action:closeDialog}]);}},
    {label:'Изменить',primary:true,action:()=>openRequirementDialog(req)},
    {label:'Архивировать',danger:true,action:async()=>{if(!confirmAction('Архивировать автомат?'))return;await api(`/requirements/${req.id}`,{method:'PATCH',body:JSON.stringify({subject_id:req.subject_id,title:req.title,description:req.description,valid_until:req.valid_until,status:'archived',revision:req.revision,items:req.items.map(requirementPayloadItem)})});closeDialog();toast('Автомат архивирован');renderView();}},
    {label:'Удалить навсегда',danger:true,action:async()=>{if(!confirmAction(`Удалить автомат «${req.title}» НАВСЕГДА? Все условия и прогресс студентов по ним будут удалены. Восстановить запись будет нельзя.`))return;await api(`/requirements/${req.id}/permanent`,{method:'DELETE'});closeDialog();toast('Автомат удалён навсегда');renderView();}},
    {label:'Закрыть',action:closeDialog}
  ]);
}

function requirementConditionEditorRow(item={},index=0) {
  const type=item.item_type||'checkbox'; const target=Number(item.target_value||1); const max=item.max_value==null?'':Number(item.max_value); const unit=item.unit||'';
  const grade=requirementTargetGrade(item);
  const typeOptions=[['checkbox','✓','Галочка'],['counter','#','Количество'],['percent','%','Процент'],['score','★','Балл']];
  return `<div class="requirement-condition-row" data-condition-index="${index}" data-target-grade="${grade}" ${item.id?`data-item-id="${escapeHTML(item.id)}"`:''}>
    <div class="condition-editor-head">
      <span class="condition-number">${index+1}</span>
      <input class="condition-title-input" data-cond="title" maxlength="300" value="${escapeHTML(item.title||'')}" placeholder="Например, сдать лабораторные">
      <button class="condition-remove" type="button" data-remove-condition aria-label="Удалить условие">×</button>
    </div>
    <div class="condition-grade-picker" role="radiogroup" aria-label="На какую оценку">
      ${[5,4,3].map((value)=>`<button type="button" class="condition-grade-option ${grade===value?'active':''}" data-cond="target_grade" data-value="${value}" aria-pressed="${grade===value?'true':'false'}">на ${value}</button>`).join('')}
    </div>
    <div class="condition-type-picker" role="radiogroup" aria-label="Тип условия">
      ${typeOptions.map(([value,icon,label])=>`<button type="button" class="condition-type-option ${type===value?'active':''}" data-cond="item_type" data-value="${value}" aria-pressed="${type===value?'true':'false'}"><span>${icon}</span><b>${label}</b></button>`).join('')}
    </div>
    <div class="condition-numeric-fields" data-condition-numeric-wrap>
      <label data-cond-numeric><span>Нужно</span><input data-cond="target_value" type="number" min="0.01" step="0.1" value="${target}"></label>
      <label data-cond-numeric><span>Из</span><input data-cond="max_value" type="number" min="0.01" step="0.1" value="${max}" placeholder="—"></label>
      <label data-cond-numeric><span>Единица</span><input data-cond="unit" maxlength="32" value="${escapeHTML(unit)}" placeholder="лабы, %, баллы"></label>
    </div>
  </div>`;
}

function syncRequirementLaneEmpty(lane) {
  if (!lane) return;
  lane.classList.toggle('is-empty', $$('.requirement-condition-row', lane).length===0);
}

function remumberRequirementConditions() {
  $$('#requirement-condition-editor .requirement-condition-row').forEach((row,index)=>{
    const number=row.querySelector('.condition-number');
    if (number) number.textContent=index+1;
  });
}

function mountRequirementConditionBuilder(initialItems=[]) {
  const root=$('#requirement-condition-editor');
  if (!root) return;
  let nextIndex=0;
  const listFor=(grade)=>root.querySelector(`[data-grade-list="${grade}"]`);
  const mountRow=(item={})=>{
    const grade=requirementTargetGrade(item);
    const list=listFor(grade);
    if (!list) return;
    const wrap=document.createElement('div');
    wrap.innerHTML=requirementConditionEditorRow(item,nextIndex++);
    const row=wrap.firstElementChild;
    list.append(row);
    const numeric=row.querySelector('[data-condition-numeric-wrap]');
    const sync=()=>{
      const type=row.querySelector('[data-cond="item_type"].active')?.dataset.value||'checkbox';
      const isCheck=type==='checkbox';
      numeric.hidden=isCheck;
      $$('input', numeric).forEach((input)=>{input.disabled=isCheck;});
    };
    $$('[data-cond="item_type"]', row).forEach((button)=>{
      button.addEventListener('click',()=>{
        $$('[data-cond="item_type"]', row).forEach((other)=>{
          const on=other===button;
          other.classList.toggle('active', on);
          other.setAttribute('aria-pressed', on?'true':'false');
        });
        sync();
      });
    });
    $$('[data-cond="target_grade"]', row).forEach((button)=>{
      button.addEventListener('click',()=>{
        const next=Number(button.dataset.value)||5;
        const prev=Number(row.dataset.targetGrade)||5;
        $$('[data-cond="target_grade"]', row).forEach((other)=>{
          const on=other===button;
          other.classList.toggle('active', on);
          other.setAttribute('aria-pressed', on?'true':'false');
        });
        row.dataset.targetGrade=String(next);
        if (next!==prev) {
          const dest=listFor(next);
          if (dest) dest.append(row);
          remumberRequirementConditions();
          $$('.requirement-grade-lane', root).forEach(syncRequirementLaneEmpty);
        }
      });
    });
    row.querySelector('[data-remove-condition]').addEventListener('click',()=>{
      row.remove();
      remumberRequirementConditions();
      syncRequirementLaneEmpty(list.closest('.requirement-grade-lane'));
    });
    sync();
    syncRequirementLaneEmpty(list.closest('.requirement-grade-lane'));
  };
  $$('.requirement-grade-lane', root).forEach(syncRequirementLaneEmpty);
  const seed=initialItems.length?initialItems:[{title:'',required:true,item_type:'checkbox',target_grade:5,target_value:1,max_value:1,unit:''}];
  seed.forEach(mountRow);
  $$('[data-add-grade-condition]', root).forEach((button)=>{
    button.addEventListener('click',()=>mountRow({required:true,item_type:'checkbox',target_grade:Number(button.dataset.addGradeCondition)||5}));
  });
}

function collectRequirementConditions() {
  return $$('#requirement-condition-editor .requirement-condition-row').map((row)=>{
    const type=row.querySelector('[data-cond="item_type"].active')?.dataset.value||'checkbox';
    const title=row.querySelector('[data-cond="title"]')?.value.trim()||'';
    if (!title) return null;
    const targetInput=row.querySelector('[data-cond="target_value"]');
    const maxInput=row.querySelector('[data-cond="max_value"]');
    const unitInput=row.querySelector('[data-cond="unit"]');
    let target=Number(targetInput?.value||1);
    const maxRaw=maxInput?.value;
    let max=maxRaw===''||maxRaw==null?null:Number(maxRaw);
    let unit=(unitInput?.value||'').trim();
    if (type==='checkbox') { target=1; max=1; unit=''; }
    if (type==='percent' && max===null) max=100;
    const item={title,required:true,item_type:type,target_grade:requirementTargetGrade({target_grade:row.dataset.targetGrade}),target_value:target,max_value:max,unit};
    if (row.dataset.itemId) item.id=row.dataset.itemId;
    return item;
  }).filter(Boolean);
}

function requirementBuilderHTML() {
  const lanes=[[5,'отлично','Что нужно закрыть на пятёрку'],[4,'хорошо','Отдельный набор на четвёрку'],[3,'удовлетворительно','Минимум на тройку']];
  return `<section class="requirement-builder">
    <div class="requirement-builder-head"><div><strong>Градация по оценкам</strong><p>Свои условия на 5, 4 и 3. Оценка — высшая, у которой выполнено всё. Если ни один набор не закрыт — 2.</p></div></div>
    <div id="requirement-condition-editor" class="requirement-grade-lanes">${lanes.map(([grade,word,hint])=>`<section class="requirement-grade-lane is-empty" data-grade="${grade}"><header class="requirement-grade-lane-head"><div><span class="requirement-grade-badge">${grade}</span><div><strong>На ${grade} · ${word}</strong><small>${hint}</small></div></div><button class="button small" type="button" data-add-grade-condition="${grade}">＋ Условие</button></header><div class="requirement-grade-lane-list" data-grade-list="${grade}"></div><p class="requirement-grade-lane-empty">Пока пусто — эта оценка не выдаётся</p></section>`).join('')}</div>
  </section>`;
}

function showRequirementFormError(message, focusSelector) {
  const box=$('#requirement-form-error');
  if (box) { box.hidden=!message; box.textContent=message||''; }
  if (message) {
    toast(message, 'err');
    if (focusSelector) $(focusSelector)?.focus();
    box?.scrollIntoView({block:'nearest',behavior:'smooth'});
  }
}

var requirementSaveBusy = false;

async function saveRequirementFromDialog(req=null) {
  if (requirementSaveBusy) return;
  const form=$('#requirement-form');
  if (!form) { toast('Форма автомата не найдена', 'err'); return; }
  const title=(form.querySelector('[name="title"]')?.value||'').trim();
  if (title.length<2) { showRequirementFormError('Введите название автомата', '[name="title"]'); return; }
  let subjectID=(form.querySelector('[name="subject_id"]')?.value||'').trim();
  const fallback=(form.querySelector('[name="subject_fallback"]')?.value||'').trim();
  if (fallback) subjectID=fallback;
  if (!subjectID) { showRequirementFormError('Выберите предмет', '[name="subject_fallback"]'); return; }
  const items=collectRequirementConditions();
  if (!items.length) { showRequirementFormError('Добавьте хотя бы одно условие с названием'); return; }
  const bad=items.find((item)=>!Number.isFinite(item.target_value)||item.target_value<=0||(item.max_value!==null&&(!Number.isFinite(item.max_value)||item.max_value<item.target_value))||![3,4,5].includes(item.target_grade));
  if (bad) { showRequirementFormError('Проверьте числа в условиях автомата'); return; }
  const validRaw=form.querySelector('[name="valid_until"]')?.value||'';
  let validUntil=null;
  if (validRaw) {
    const date=new Date(validRaw);
    if (!Number.isFinite(date.getTime())) { showRequirementFormError('Проверьте дату окончания', '[name="valid_until"]'); return; }
    validUntil=date.toISOString();
  }
  const payload={subject_id:subjectID,title,description:form.querySelector('[name="description"]')?.value||'',valid_until:validUntil,items};
  if (req) { payload.revision=req.revision; payload.status=req.status||'active'; }
  requirementSaveBusy=true;
  form.querySelectorAll('[type="submit"], .requirement-save-button').forEach((button)=>{button.disabled=true;});
  showRequirementFormError('');
  try {
    await api(req?`/requirements/${req.id}`:`/groups/${state.groupID}/requirements`,{method:req?'PATCH':'POST',body:JSON.stringify(payload)});
    closeDialog();
    toast('Автомат сохранён', 'ok');
    renderView();
  } finally {
    requirementSaveBusy=false;
    $$('#requirement-form [type="submit"], #requirement-form .requirement-save-button, #dialog-footer [form="requirement-form"]').forEach((button)=>{button.disabled=false;});
  }
}

function wireRequirementForm(req=null) {
  const form=$('#requirement-form');
  if (!form) return;
  form.addEventListener('submit', (event) => {
    event.preventDefault();
    event.stopPropagation();
    saveRequirementFromDialog(req).catch((error) => {
      console.error(error);
      showRequirementFormError(error?.message || 'Не удалось сохранить автомат');
    });
  });
  if (req) {
    if (form.subject_fallback) form.subject_fallback.value=req.subject_id;
    form.title.value=req.title;
    if (form.description) form.description.value=req.description||'';
    if (form.valid_until) form.valid_until.value=req.valid_until?isoLocal(new Date(req.valid_until)):'';
  }
}

export async function openRequirementDialog(req=null, selectedEntry=null){
  await loadCatalogs();
  const subjectID=selectedEntry?.subject_id || req?.subject_id || '';
  const subjectOptions=`<label>Предмет<select name="subject_fallback"><option value="">Выберите предмет</option>${options(state.catalogs.subjects.filter(v=>v.status!=='archived'),'id',v=>v.name)}</select><small class="optional">Автомат относится ко всему предмету, а не к отдельной паре.</small></label>`;
  const commonStart=`<input type="hidden" name="subject_id" value="${escapeHTML(subjectID)}">${subjectOptions}`;
  const builder=requirementBuilderHTML();
  const actions=`<div class="requirement-form-actions"><p id="requirement-form-error" class="notice error" hidden></p><button class="button primary requirement-save-button" type="submit">Сохранить автомат</button></div>`;
  const formHTML=contextMode()?`<form class="form requirement-form x10-form" id="requirement-form" novalidate>${commonStart}<label>Название<input name="title" maxlength="200" placeholder="Автомат по физике"></label>${builder}<details class="x10-more"><summary>Дополнительно</summary><div><label>Описание<textarea name="description" maxlength="10000" placeholder="Общие условия и комментарии"></textarea></label><label>Действует до<input name="valid_until" type="datetime-local"></label></div></details>${actions}</form>`:`<form class="form requirement-form" id="requirement-form" novalidate>${commonStart}<label>Название<input name="title" maxlength="200" placeholder="Например: Автомат по физике"></label><label>Описание<textarea name="description" maxlength="10000" placeholder="Общие условия и комментарии"></textarea></label>${builder}<label>Действует до<input name="valid_until" type="datetime-local"></label>${actions}</form>`;
  openDialog(req?'Изменить автомат':'Новый автомат',formHTML,[
    {label:'Отмена',action:closeDialog},
    {label:'Сохранить',primary:true,type:'submit',form:'requirement-form'},
  ]);
  const f=$('#requirement-form');
  wireRequirementForm(req);
  mountRequirementConditionBuilder(req?.items||[]);
}

export function initRequirementsRuntime() {
  // This domain has no eager DOM side effects.
}
